import { useState } from 'react';

import {
  PeerParticipants,
  useListCampaignPeers,
  useShowConsumerCampaignById,
} from '@givebacks/api/store';
import { useBreakPoints } from 'core';
import { useNavigate } from 'react-router-dom';
import { ButtonMantine, Grid, Group, Skeleton, Stack, Title } from 'ui';

import { useCrowdfundingProvider } from '../../../../Context';
import { CampaignPeerInfoCard, CampaignPeerSearchModal } from '../../../components';

const ViewAllParticipants = ({
  teamName,
  participants,
}: {
  teamName: string;
  participants: PeerParticipants;
}) => {
  const { isSmallScreen } = useBreakPoints();
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <>
      <ButtonMantine
        size={isSmallScreen ? 'xs' : 'sm'}
        variant="subtle"
        onClick={() => setIsModalOpen(true)}
      >
        View All ({participants?.length})
      </ButtonMantine>
      <CampaignPeerSearchModal
        type="participants"
        opened={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        teamName={teamName}
        togglePeerTypes={false}
        peerPath={`../participant-center/participants`}
      />
    </>
  );
};

const TeamParticipants = ({ teamName }: { teamName: string }) => {
  const { causeId, campaignId } = useCrowdfundingProvider();
  const { data: campaign } = useShowConsumerCampaignById({ causeId, campaignId });
  const navigate = useNavigate();

  const { data: peerResponse, isLoading } = useListCampaignPeers({
    peer_type: 'participants',
    campaignId: campaignId,
    causeId: causeId,
    queryOptions: {
      searches: {
        'team.name': { value: teamName },
      },
      order: {
        field: 'rank',
        direction: 'asc',
      },
    },
  });

  if (!peerResponse?.participants.length) return null;
  return (
    <Stack>
      <Group justify="space-between">
        <Title order={2}>{campaign?.participant_options.convention_plural}</Title>
        {peerResponse.participants.length > 3 && (
          <ViewAllParticipants teamName={teamName} participants={peerResponse.participants} />
        )}
      </Group>
      <Grid>
        {peerResponse.participants?.slice(0, 3)?.map((participant) => {
          return (
            <Grid.Col span={{ base: 12, md: 4 }} key={participant.uuid}>
              <CampaignPeerInfoCard
                type="participants"
                peer_id={participant.uuid}
                campaignId={campaignId}
                causeId={causeId}
                onClick={() => navigate(`../participant-center/participants/${participant.uuid}`)}
              />
            </Grid.Col>
          );
        })}
        {isLoading && (
          <Grid w="100%">
            {[...Array(3)].map((_, index) => (
              <Grid.Col span={{ base: 12, md: 4 }} key={index}>
                <Skeleton
                  visible
                  h={{
                    base: 94,
                    md: 208,
                  }}
                  w="100%"
                />
              </Grid.Col>
            ))}
          </Grid>
        )}
      </Grid>
    </Stack>
  );
};

export { TeamParticipants };
